import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  DestroyRef,
  inject,
  LOCALE_ID,
  OnInit,
  signal,
} from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { DatePipe, formatDate } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { forkJoin } from 'rxjs';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';

import { KickstartService } from '../../../../core/kickstart/kickstart.service';
import {
  DIRECT_APPLICATIONS_PAUSED,
  preApplicationLink,
} from '../../../../core/applications/direct-applications';
import { KsApplication, hasConfirmedAmount } from '../../../../core/kickstart/kickstart.types';
import { EntityService } from '../../../../core/entity/entity.service';
import { EntityDetail } from '../../../../core/entity/entity.types';
import { IconComponent } from '../../../../shared/icon/icon';
import { StatusChipComponent } from '../../../../shared/status-chip/status-chip';
import { ZarPipe } from '../../../../shared/pipes/zar.pipe';
import { DashPipe } from '../../../../shared/pipes/dash.pipe';
import { AgoPipe } from '../../../../shared/pipes/ago.pipe';

@Component({
  selector: 'app-ks-list',
  imports: [
    MatCardModule,
    MatButtonModule,
    MatTableModule,
    MatTooltipModule,
    RouterLink,
    DatePipe,
    IconComponent,
    StatusChipComponent,
    ZarPipe,
    DashPipe,
    AgoPipe,
  ],
  templateUrl: './ks-list.html',
  styleUrl: './ks-list.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class KsListComponent implements OnInit {
  private readonly kickstartService = inject(KickstartService);
  private readonly entityService = inject(EntityService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);
  private readonly locale = inject(LOCALE_ID);

  protected readonly entityId = this.route.snapshot.paramMap.get('id') ?? '';
  protected readonly entity = signal<EntityDetail | null>(null);
  protected readonly applications = signal<KsApplication[]>([]);
  protected readonly hasConfirmedAmount = hasConfirmedAmount;
  protected readonly isLoading = signal(true);
  protected readonly loadError = signal(false);

  protected readonly columns = ['entity', 'amount', 'status', 'stage', 'started', 'actions'];

  ngOnInit(): void {
    this.load();
  }

  /**
   * Hover summary for the Started cell — the row's full timeline in one place:
   * when it was started, last edited, and submitted. Keeps the table to a single
   * date column while still surfacing the other timestamps on demand.
   */
  protected activityTooltip(app: KsApplication): string {
    const fmt = (value: string | null): string =>
      value ? formatDate(value, 'medium', this.locale) : 'Not yet submitted';
    return [
      `Started: ${formatDate(app.created_at, 'medium', this.locale)}`,
      `Last worked on: ${formatDate(app.updated_at, 'medium', this.locale)}`,
      `Submitted: ${fmt(app.submitted_at)}`,
    ].join('\n');
  }

  /** Paused 2026-10-22 (FLOW-394): direct applications go through the pre-application. */
  protected readonly directApplicationsPaused = DIRECT_APPLICATIONS_PAUSED;

  /** The applicant's open, resumable draft for this business, if any. */
  protected readonly openDraft = computed(() =>
    this.applications().find((a) => a.application_stage === 'DRAFT'),
  );

  /**
   * Start a new application — but funnel back to an existing open draft rather
   * than spawning a second one. Prevents orphan drafts accumulating when a user
   * abandons the wizard and later clicks "New" again.
   */
  protected startOrResume(): void {
    const draft = this.openDraft();
    void this.router.navigate(
      draft
        ? ['/entity', this.entityId, 'kickstart', draft.short_id]
        : this.newApplicationLink(),
    );
  }

  /** Paused 2026-10-22 (FLOW-394): direct applications go through the pre-application. */
  private newApplicationLink(): string[] {
    return this.directApplicationsPaused
      ? preApplicationLink(this.entityId)
      : ['/entity', this.entityId, 'kickstart', 'new'];
  }

  protected reload(): void {
    this.loadError.set(false);
    this.isLoading.set(true);
    this.load();
  }

  private load(): void {
    forkJoin({
      entity: this.entityService.getEntity(this.entityId),
      applications: this.kickstartService.listForEntity(this.entityId),
    })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: ({ entity, applications }) => {
          this.entity.set(entity);
          this.applications.set(applications.applications);

          // Smart entry: a business with no Kickstart Cash applications has nothing
          // to list, so send them straight to the form. `replaceUrl` keeps Back
          // returning to the dashboard rather than flashing this empty list.
          if (applications.applications.length === 0) {
            void this.router.navigate(this.newApplicationLink(), {
              replaceUrl: true,
            });
            return;
          }

          this.isLoading.set(false);
          this.cdr.markForCheck();
        },
        error: () => {
          this.isLoading.set(false);
          this.loadError.set(true);
          this.cdr.markForCheck();
        },
      });
  }
}
